import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Empty,
  Input,
  message,
  Modal,
  notification,
  Popover,
  Select,
  Space,
  Table,
  Tabs,
  Tooltip,
} from "antd";
import { DownloadOutlined, FilterOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { uniqueMergeArrays } from "functions/arrays";
import utils from "utils";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import Flex from "components/shared-components/Flex";
import Loading from "components/shared-components/Loading";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { PRODUCT_COMPANY_GROUPS, PRODUCT_GROUPS } from "../../../../constants/ProductConstant";
import { carryOverStock, stockSyncExact } from "urls";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import axios from "axios";
import { updateCountedStock } from "../../../../functions/api/stock";
import { cartStockLines } from "../../../../functions/productStock";
import { useStockData } from "../../../../data/hooks/firestore/useStock";
import { isMobile } from "../../../../functions/browser";
import useIsMobile from "../../../../hooks/useIsMobile";
import BatchStockUpdateModal from "./modals/BatchStockUpdateModal";
import StockChangeReasonModal from "./modals/StockChangeReasonModal";
import CrateStocksTab from "./CrateStocksTab";
import CratesLogTab from "./CratesLogTab";
import LiveStockTab from "./LiveStockTab";
import PackagingStocksTab from "./PackagingStocksTab";
import { useCrateStocks } from "../../../../data/hooks/firestore/useCrateStocks";
import { usePackagingStock } from "../../../../data/hooks/firestore/usePackagingStock";
import AdjustCrateQuantityModal from "./modals/AdjustCrateQuantityModal";
import {
  FIRESTORE_DEFAULT_PROJECTIONS_DOC,
  FIRESTORE_PACKAGING_STOCK_SNAPSHOTS_TABLE,
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { resolveOrderLeadDays } from "../../../../functions/leadDays";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { useLeads } from "../../../../data/hooks/firestore/useLeads";
import { useCountries } from "../../../../data/hooks/firestore/useCountries";
import styled from "styled-components";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_CANCELLED } from "../../../../constants/OrderConstant";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { getUserPermissions } from "../../../../utils/permissionUtils";
import roles from "../../../../roles";
import { StyledSelect } from "../../../../styles/sharedStyles";
import StockEditPopover from "components/shared-components/StockEditPopover";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
const { Option } = Select;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

// Upper bound on configured leadDays (see country delivery config), used to
// widen the orders query so orders with a longer lead time than 1 day are
// not missed.
const MAX_LEAD_DAYS = 6;

const Stock = () => {
  const { t } = useTranslation();
  const { isLoading, stockDates, stockData, getTodayCarryOverStatus } = useStockData();
  const { products, isLoading: productsLoading } = useProducts();
  const { crateStocks, updateCrateStock, addCrateEvent } = useCrateStocks();
  const { packagingStocks } = usePackagingStock();
  // { [dateId]: { [barcode]: stock } }, written daily at 05:00 by the api
  const [packagingSnapshots, setPackagingSnapshots] = useState({});
  // All customers incl. "new" (not yet accepted): their orders also claim stock.
  const { allCustomers: customers } = useCustomers();
  const { leads } = useLeads();
  const { getDeliveryLabelLeadDays } = useCountries();
  const [activeTab, setActiveTab] = useState("products");
  const [productList, setProductList] = useState([]);
  const [ordersInDateRange, setOrdersInDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const categories = uniqueMergeArrays(products, "productCategory");

  const productsByProductId = useMemo(() => new Map((products || []).map((p) => [p.productId, p])), [products]);
  const countInCart = (cart, productId) =>
    cartStockLines(cart, productsByProductId).get(productId) || { direct: 0, viaBundle: 0 };
  const [clickedCategory, setClickedCategory] = useState(localStorage.getItem("categoryFilter") || "All");
  const [clickedProductCompany, setClickedProductCompany] = useState(
    localStorage.getItem("productCompanyFilter") || "All",
  );
  const [clickedProductGroup, setClickedProductGroup] = useState(localStorage.getItem("productGroupFilter") || "All");
  const [activeTimeFilter, setActiveTimeFilter] = useState("week");
  const [filterByInSeason, setFilterByInSeason] = useState(
    localStorage.getItem("filterByInSeason") ? localStorage.getItem("filterByInSeason") === "true" : true,
  );
  const [batchUpdateModalVisible, setBatchUpdateModalVisible] = useState(false);
  const [customDatePickerVisible, setCustomDatePickerVisible] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(null);
  const isMounted = useRef(false);
  const [syncingToExact, setSyncingToExact] = useState(false);
  const [carryingOverStock, setCarryingOverStock] = useState(false);

  // Product crates drawer state
  const [productCratesDrawerOpen, setProductCratesDrawerOpen] = useState(false);
  const [selectedProductForCrates, setSelectedProductForCrates] = useState(null);
  const [productAdjustModalOpen, setProductAdjustModalOpen] = useState(false);
  const [productAdjustingCrate, setProductAdjustingCrate] = useState(null);
  const [productAdjustingCrateItem, setProductAdjustingCrateItem] = useState(null);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();

  // Crate drawer cross-navigation
  const [crateDrawerOpenForId, setCrateDrawerOpenForId] = useState(null);

  const handleProductClickFromCrateDrawer = ({ productId, productName }) => {
    setSelectedProductForCrates({ key: productId, productName, productId });
    setProductCratesDrawerOpen(true);
  };

  // Dynamic table height state
  const tableContainerRef = useRef(null);
  const [tableY, setTableY] = useState(600);

  // State for tracking additional date fetching
  const [needsAdditionalFetch, setNeedsAdditionalFetch] = useState(false);
  const [additionalDateRange, setAdditionalDateRange] = useState(null);
  const [additionalFetchKey, setAdditionalFetchKey] = useState("");
  const [additionalStockDataLocal, setAdditionalStockData] = useState([]);
  const additionalStockData = additionalStockDataLocal;

  // Popover state for editing stock values
  const [editingData, setEditingData] = useState(null);
  const [editingValue, setEditingValue] = useState(0);
  const [popoverVisibleMap, setPopoverVisibleMap] = useState({});
  const [stockReasonModalOpen, setStockReasonModalOpen] = useState(false);
  const [pendingStockUpdate, setPendingStockUpdate] = useState(null);

  // Order fetched in date range
  const [dateRange, setDateRange] = useState(null);
  const ordersDateRange = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return dateRange;
    return [dateRange[0], dayjs(dateRange[1]).add(MAX_LEAD_DAYS, "day")];
  }, [dateRange]);
  const { data: orders } = useOrders(null, ordersDateRange);

  const productCratesData = useMemo(() => {
    if (!selectedProductForCrates || !crateStocks) return [];
    const productId = selectedProductForCrates.key;
    return (crateStocks || [])
      .filter((crate) => Array.isArray(crate.items) && crate.items.some((item) => item.productId === productId))
      .map((crate) => {
        const item = crate.items.find((item) => item.productId === productId);
        return {
          key: crate.id,
          crateId: crate.crateId,
          packageDate: item?.packageDate || "-",
          quantity: item?.quantity ?? 0,
          _crate: crate,
          _crateItem: item,
        };
      });
  }, [selectedProductForCrates, crateStocks]);

  // Permissions
  const { role: currentUserRole, uid } = useAuth();
  const [userPermissions, setUserPermissions] = useState({});

  useEffect(() => {
    const loadPermissions = async () => {
      if (currentUserRole === roles.superAdmin) {
        setUserPermissions({ edit: true });
      } else if (currentUserRole === roles.admin) {
        const permissions = await getUserPermissions(uid, currentUserRole);
        setUserPermissions(permissions["shop-products-stock"] || { view: false, edit: false });
      }
    };
    loadPermissions();
  }, [currentUserRole, uid]);

  const hasEditPermission = currentUserRole === roles.superAdmin || userPermissions.edit === true;

  useEffect(() => {
    let unsubscribe = null;

    if (needsAdditionalFetch && additionalDateRange && additionalFetchKey) {
      const startUnix = dayjs(additionalDateRange[0]).startOf("day").unix();
      const endUnix = dayjs(additionalDateRange[1]).endOf("day").unix();

      // Manual Firestore listener
      const stockQuery = query(
        collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_STOCK_TABLE),
        where("dateUnix", ">=", startUnix),
        where("dateUnix", "<=", endUnix),
        orderBy("dateUnix", "asc"),
      );
      unsubscribe = onSnapshot(
        stockQuery,
        (snapshot) => {
          setAdditionalStockData(snapshot.docs);
        },
        (error) => {
          console.error("Error in additional data listener:", error);
        },
      );
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [needsAdditionalFetch, additionalDateRange, additionalFetchKey]);

  // ponytail: listens to the whole snapshot collection (one small doc per day), query by dateUnix if it grows too large
  useEffect(() => {
    return onSnapshot(
      collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PACKAGING_STOCK_SNAPSHOTS_TABLE),
      (snapshot) => {
        const byDate = {};
        snapshot.docs.forEach((doc) => {
          byDate[doc.id] = doc.data().stocks || {};
        });
        setPackagingSnapshots(byDate);
      },
      (error) => console.error("Error listening to packaging stock snapshots:", error),
    );
  }, []);

  // Check if date range needs additional fetching
  const checkIfNeedsAdditionalFetch = (selectedDateRange) => {
    if (!selectedDateRange || selectedDateRange.length !== 2) {
      setNeedsAdditionalFetch(false);
      setAdditionalDateRange(null);
      setAdditionalFetchKey("");
      return false;
    }

    const selectedStart = dayjs(selectedDateRange[0]).startOf("day");
    const selectedEnd = dayjs(selectedDateRange[1]).endOf("day");

    // Get context date range (28 days past to 28 days future)
    const contextStart = dayjs().subtract(28, "days").startOf("day");
    const contextEnd = dayjs().add(28, "days").endOf("day");

    // Check if selected range extends beyond context range
    const needsFetch = selectedStart.isBefore(contextStart) || selectedEnd.isAfter(contextEnd);

    if (needsFetch) {
      // Create a unique key to avoid conflicts
      const fetchKey = `${selectedStart.format("YYYYMMDD")}_${selectedEnd.format("YYYYMMDD")}`;
      setNeedsAdditionalFetch(true);
      setAdditionalDateRange(selectedDateRange);
      setAdditionalFetchKey(fetchKey);
      return true;
    } else {
      setNeedsAdditionalFetch(false);
      setAdditionalDateRange(null);
      setAdditionalFetchKey("");
      return false;
    }
  };

  // Filter orders based on selected date range. Delivery date = packing
  // date + leadDays, where leadDays depends on the order's delivery label
  // and its country's configured lead time (defaults to 1 day).
  const filterOrdersByDateRange = (dateRange) => {
    if (!orders || !dateRange || dateRange.length !== 2) {
      setOrdersInDateRange([]);
      return;
    }

    const startDate = dayjs(dateRange[0]).startOf("day");
    const endDate = dayjs(dateRange[1]).endOf("day");

    // Filter orders whose actual packing date (delivery date - that order's
    // leadDays) falls within the selected packing date range.
    const filteredOrders = orders
      .filter((order) => !!order.farmorderDeliveryDate)
      .map((order) => {
        const leadDays = resolveOrderLeadDays(order, {
          orders,
          customers,
          leads,
          getDeliveryLabelLeadDays,
        });
        const packingDate = dayjs(order.farmorderDeliveryDate).subtract(leadDays, "day");
        return {
          ...order,
          packingDateId: packingDate.format("DDMMYYYY"),
          _packingDate: packingDate,
        };
      })
      .filter(
        ({ _packingDate }) =>
          _packingDate.isSameOrAfter(startDate, "day") && _packingDate.isSameOrBefore(endDate, "day"),
      )
      .map(({ _packingDate, ...order }) => order);

    setOrdersInDateRange(filteredOrders);
  };

  // Get all available dates and data (but keep sources separate)
  const getAllAvailableData = () => {
    let allDates = [...stockDates];

    // Add additional dates if available (but don't combine the data)
    if (additionalStockData && additionalStockData.length > 0) {
      additionalStockData.forEach((doc) => {
        // Check if it's a Firestore document or already processed data
        const data = typeof doc.data === "function" ? doc.data() : doc;
        const docId = doc.id || data.id;

        if (docId !== FIRESTORE_DEFAULT_PROJECTIONS_DOC) {
          // Check if this date is not already in context data
          const existingDate = allDates.find((d) => d.id === docId);
          if (!existingDate) {
            allDates.push({
              id: docId,
              date: data.date,
              dateUnix: data.dateUnix,
              formattedDate: dayjs(data.dateUnix * 1000).format("DD-MM-YYYY"),
            });
          }
        }
      });

      // Sort all dates
      allDates.sort((a, b) => a.dateUnix - b.dateUnix);
    }
    return { allDates };
  };

  // Get stock data for a specific date (check both sources)
  const getStockDataForDate = (dateId, productId) => {
    // First check context data
    if (stockData[dateId] && stockData[dateId][productId]) {
      return stockData[dateId][productId];
    }

    // Then check additional data
    if (additionalStockData && additionalStockData.length > 0) {
      const additionalDoc = additionalStockData.find((doc) => {
        const docId = doc.id || (typeof doc.data === "function" ? doc.id : doc.id);
        return docId === dateId;
      });

      if (additionalDoc) {
        const data = typeof additionalDoc.data === "function" ? additionalDoc.data() : additionalDoc;
        return (
          data.products?.[productId] || {
            projectedStock: 0,
            countedStock: 0,
            requestedStock: 0,
            currentStock: 0,
          }
        );
      }
    }

    // Default fallback
    return {
      projectedStock: 0,
      countedStock: 0,
      requestedStock: 0,
      currentStock: 0,
    };
  };

  // First load of data - transform the product data
  useEffect(() => {
    if (products && products.length > 0) {
      const { allDates } = getAllAvailableData();

      if (allDates.length > 0) {
        transformProductData();

        // If we have data and no date range set, apply the week filter
        if (!dateRange) {
          setTimeout(() => {
            applyTimeFilter("today");
          }, 100);
        }
      }
    }
  }, [products, stockData, stockDates, additionalStockData, packagingStocks, packagingSnapshots]);

  useEffect(() => {
    isMounted.current = true;
    // Compute dynamic table height on mount and when resizing
    const updateTableHeight = () => {
      try {
        if (tableContainerRef.current) {
          const rect = tableContainerRef.current.getBoundingClientRect();
          // Leave a small bottom margin to avoid overlapping footer
          const bottomOffset = 24; // px
          const computed = window.innerHeight - rect.top - bottomOffset;
          setTableY(Math.max(200, Math.floor(computed))); // enforce a sensible minimum
        }
      } catch (e) {
        // Fallback to default on any error
        setTableY(600);
      }
    };
    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => {
      isMounted.current = false;
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && products?.length > 0 && productList?.length > 0) {
      setTimeout(() => {
        applyTimeFilter("today");
      }, 300);
    }
  }, [isLoading, productsLoading]);

  const transformProductData = () => {
    const { allDates } = getAllAvailableData();

    const transformedProducts = products.map((product) => {
      const productData = {
        key: product.id,
        productId: product.productId,
        productName: product.productName || product.productNameNL,
        productCategory: product.productCategory || product.productCategoryNL,
        productCompany: product.company,
        productGroup: product.productGroup,
        productInSeason: product.productInSeason,
      };

      // Add stock data for each date (using separate data sources)
      allDates.forEach((date) => {
        const productStock = getStockDataForDate(date.id, product.id);
        productData[`${date.id}_projected`] = productStock.projectedStock;
        productData[`${date.id}_counted`] = productStock.countedStock;
        productData[`${date.id}_requested`] = productStock.requestedStock;
        productData[`${date.id}_current`] = productStock.currentStock;
      });

      return productData;
    });

    // Packaging (shipping boxes) is not a product and has a single stock number, so it is shown read-only as counted stock:
    // live for today, the 05:00 snapshot for past dates, nothing for future dates
    packagingStocks.forEach((packaging) => {
      const packagingData = {
        key: `packaging_${packaging.barcode}`,
        productId: packaging.barcode,
        productName: packaging.name,
        productGroup: "Packaging",
        productInSeason: true,
        isPackaging: true,
      };
      allDates.forEach((date) => {
        const day = dayjs(date.dateUnix * 1000);
        if (day.isSame(dayjs(), "day")) {
          packagingData[`${date.id}_counted`] = packaging.stock;
        } else if (day.isBefore(dayjs(), "day")) {
          packagingData[`${date.id}_counted`] = packagingSnapshots[date.id]?.[packaging.barcode];
        }
      });
      transformedProducts.push(packagingData);
    });

    setProductList(transformedProducts);

    // If we already have filters, apply them
    if (
      searchText ||
      clickedCategory !== "All" ||
      clickedProductCompany !== "All" ||
      clickedProductGroup !== "All" ||
      dateRange
    ) {
      applyFilters(
        transformedProducts,
        searchText,
        clickedCategory,
        clickedProductCompany,
        clickedProductGroup,
        dateRange,
      );
    } else {
      setFilteredProducts(transformedProducts);
    }
  };

  // Filters and search
  const onSearch = (value) => {
    setSearchText(value);
    applyFilters(
      productList,
      value,
      clickedCategory,
      clickedProductCompany,
      clickedProductGroup,
      dateRange,
      filterByInSeason,
    );
  };

  const handleShowCategory = (value) => {
    localStorage.setItem("categoryFilter", value);
    setClickedCategory(value);
    applyFilters(
      productList,
      searchText,
      value,
      clickedProductCompany,
      clickedProductGroup,
      dateRange,
      filterByInSeason,
    );
  };

  const handleShowProductCompany = (value) => {
    localStorage.setItem("productCompanyFilter", value);
    setClickedProductCompany(value);
    applyFilters(productList, searchText, clickedCategory, value, clickedProductGroup, dateRange, filterByInSeason);
  };

  const handleShowProductGroup = (value) => {
    localStorage.setItem("productGroupFilter", value);
    setClickedProductGroup(value);
    applyFilters(productList, searchText, clickedCategory, clickedProductCompany, value, dateRange, filterByInSeason);
  };

  const handleFilterByInSeasonChange = (value) => {
    localStorage.setItem("filterByInSeason", value);
    setFilterByInSeason(value);
    applyFilters(
      productList,
      searchText,
      clickedCategory,
      clickedProductCompany,
      clickedProductGroup,
      dateRange,
      value,
    );
  };

  const handleDateRangeChange = (dates) => {
    // Only process if we have both start and end dates
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setDateRange(dates);
      setActiveTimeFilter("custom");

      // Check if we need additional fetching
      checkIfNeedsAdditionalFetch(dates);
      applyFilters(
        productList,
        searchText,
        clickedCategory,
        clickedProductCompany,
        clickedProductGroup,
        dates,
        filterByInSeason,
      );

      // Filter orders based on the new date range
      filterOrdersByDateRange(dates);
    }
  };

  // Apply time filter (week, month)
  const applyTimeFilter = (filter) => {
    const today = dayjs().startOf("day");
    let endDate;

    if (filter === "week") {
      endDate = dayjs().add(6, "days").endOf("day");
    } else if (filter === "month") {
      endDate = dayjs().add(27, "days").endOf("day");
    } else if (filter === "today") {
      endDate = dayjs().endOf("day");
    } else {
      return;
    }

    const newDateRange = [today, endDate];

    setDateRange(newDateRange);
    setActiveTimeFilter(filter);

    // Check if we need additional fetching (likely not for week/month within 28 days)
    checkIfNeedsAdditionalFetch(newDateRange);

    if (productList && productList.length > 0) {
      const filtered = applyFiltersDirectly(
        productList,
        searchText,
        clickedCategory,
        clickedProductCompany,
        newDateRange,
        clickedProductGroup,
        filterByInSeason,
      );
      setFilteredProducts(filtered);
    }

    // Filter orders based on the new date range
    filterOrdersByDateRange(newDateRange);
  };

  // Direct version that returns the filtered array instead of setting state
  const applyFiltersDirectly = (
    products,
    searchValue = searchText,
    category = clickedCategory,
    productCompany = clickedProductCompany,
    dateRange,
    productGroup = clickedProductGroup,
    onlyShowInSeason = filterByInSeason,
  ) => {
    const { allDates } = getAllAvailableData();
    let filtered = [...products];

    // Apply out of season filter
    if (onlyShowInSeason) {
      filtered = filtered.filter((product) => product.productInSeason === true);
    }

    // Apply category filter
    if (category && category !== "All") {
      filtered = filtered.filter((product) => product.productCategory === category);
    }

    // Apply company filter
    if (productCompany && productCompany !== "All") {
      filtered = filtered.filter((product) => product.productCompany === productCompany);
    }

    // Apply product group filter
    if (productGroup && productGroup !== "All") {
      filtered = filtered.filter((product) => product.productGroup === productGroup);
    }

    // Apply search filter
    if (searchValue) {
      filtered = filtered.filter(
        (product) =>
          (product.productId && product.productId.toLowerCase().includes(searchValue.toLowerCase())) ||
          (product.productName && product.productName.toLowerCase().includes(searchValue.toLowerCase())),
      );
    }

    // Apply date range filter
    if (dateRange && dateRange.length === 2) {
      const startDate = dayjs(dateRange[0]);
      const endDate = dayjs(dateRange[1]);

      // Find matching dates from all available dates that fall within range
      const matchingDates = allDates.filter((d) => {
        const stockDate = d.dateUnix * 1000;
        return dayjs(stockDate).isSameOrAfter(startDate, "day") && dayjs(stockDate).isSameOrBefore(endDate, "day");
      });

      if (matchingDates.length > 0) {
        // Create product objects with only the columns we need
        filtered = filtered.map((product) => {
          const filteredProduct = {
            key: product.key,
            productId: product.productId,
            productName: product.productName,
            productCategory: product.productCategory,
            productCompany: product.productCompany,
            productGroup: product.productGroup,
            productInSeason: product.productInSeason,
            isPackaging: product.isPackaging,
          };

          // Add columns for each date in range
          matchingDates.forEach((matchDate) => {
            filteredProduct[`${matchDate.id}_projected`] = product[`${matchDate.id}_projected`];
            filteredProduct[`${matchDate.id}_counted`] = product[`${matchDate.id}_counted`];
            filteredProduct[`${matchDate.id}_requested`] = product[`${matchDate.id}_requested`];
            filteredProduct[`${matchDate.id}_current`] = product[`${matchDate.id}_current`];
          });

          return filteredProduct;
        });
      }
    }

    return filtered;
  };

  const applyFilters = (
    products,
    searchValue = searchText,
    category = clickedCategory,
    productCompany = clickedProductCompany,
    productGroup = clickedProductGroup,
    dateRange = dateRange,
    onlyShowInSeason = filterByInSeason,
  ) => {
    const filtered = applyFiltersDirectly(
      products,
      searchValue,
      category,
      productCompany,
      dateRange,
      productGroup,
      onlyShowInSeason,
    );
    setFilteredProducts(filtered);
  };

  const handleCountedStockClick = (record, dateId, value) => {
    const popoverKey = `${record.productId}_${dateId}`;

    setPopoverVisibleMap({});

    setEditingData({
      productId: record.productId,
      dateId: dateId,
      record: record,
      popoverKey: popoverKey,
      currentCounted: value || 0,
    });
    // Start with a delta of 0; user will enter the difference to add/subtract
    setEditingValue(0);

    setTimeout(() => {
      setPopoverVisibleMap({
        [popoverKey]: true,
      });
    }, 0);
  };

  const handleSaveCountedStock = () => {
    if (!editingData) return;

    const current = Number(editingData.currentCounted || 0);
    const delta = Number(editingValue || 0);
    const newAbsolute = current + delta;

    // Close popover first to prevent z-index issues
    setPopoverVisibleMap({});

    // Store pending update and show reason modal
    setPendingStockUpdate({
      productId: editingData.productId,
      dateId: editingData.dateId,
      newAbsolute,
    });
    setStockReasonModalOpen(true);
  };

  const handleStockReasonConfirm = async (isIncomingProducts) => {
    if (!pendingStockUpdate) return;

    try {
      const token = await getFirebaseIDToken();

      await updateCountedStock(
        pendingStockUpdate.productId,
        pendingStockUpdate.dateId,
        pendingStockUpdate.newAbsolute,
        COMPANY_COUNTRY_CODE,
        token,
        isIncomingProducts,
      );

      message.success(t("notifications.counted_stock_updated_successfully"));
    } catch (error) {
      console.error("Error updating counted stock:", error);
      message.error(t("notifications.failed_to_update_counted_stock"));
    } finally {
      setStockReasonModalOpen(false);
      setPendingStockUpdate(null);
      setPopoverVisibleMap({});
      setEditingData(null);
    }
  };

  const handleStockReasonCancel = () => {
    setStockReasonModalOpen(false);
    setPendingStockUpdate(null);
  };

  // Build table columns
  const getTableColumns = () => {
    const { allDates } = getAllAvailableData();

    const columns = [
      {
        title: t("name"),
        dataIndex: "productName",
        key: "productName",
        width: isMobile ? 150 : 240,
        fixed: "left",
        sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
        defaultSortOrder: "descend",
        render: (text, record) =>
          record.isPackaging ? (
            text
          ) : (
            <a
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProductForCrates(record);
                setProductCratesDrawerOpen(true);
              }}
              style={{ color: "#880e47" }}
            >
              {text}
            </a>
          ),
      },
    ];

    // If there's a specific date range selected, only show columns for dates within that range
    if (dateRange && dateRange.length === 2) {
      const startDate = dayjs(dateRange[0]);
      const endDate = dayjs(dateRange[1]);

      // Find dates within the selected range
      const matchingDates = allDates.filter((d) => {
        const stockDate = d.dateUnix * 1000;
        return dayjs(stockDate).isSameOrAfter(startDate, "day") && dayjs(stockDate).isSameOrBefore(endDate, "day");
      });

      matchingDates.forEach((date, index) => {
        const isEvenColumn = index % 2 === 0;
        const columnStyle = isEvenColumn ? {} : { background: "#f9f9f9" };

        columns.push({
          title: (
            <div style={{ textAlign: "center" }}>
              <div>
                {dayjs(date.dateUnix * 1000)
                  .locale("en")
                  .format("dddd")}{" "}
                {dayjs(date.dateUnix * 1000).format("DD-MM-YYYY")}
              </div>
            </div>
          ),
          key: date.id,
          children: [
            {
              title: t("counted"),
              dataIndex: `${date.id}_counted`,
              key: `${date.id}_counted`,
              width: 110,
              render: (text, record) => {
                const popoverKey = `${record.productId}_${date.id}`;
                const isPopoverVisible = popoverVisibleMap[popoverKey] === true;
                const currentCounted =
                  editingData?.popoverKey === popoverKey ? Number(editingData.currentCounted || 0) : Number(text || 0);

                if (!hasEditPermission || record.isPackaging) {
                  return <span>{text}</span>;
                }

                return (
                  <StockEditPopover
                    visible={isPopoverVisible}
                    onVisibleChange={(visible) => {
                      if (!visible) {
                        setPopoverVisibleMap((prev) => ({
                          ...prev,
                          [popoverKey]: false,
                        }));
                        if (editingData?.popoverKey === popoverKey) {
                          setEditingData(null);
                        }
                      }
                    }}
                    currentValue={currentCounted}
                    editValue={editingValue}
                    onEditValueChange={setEditingValue}
                    onSave={handleSaveCountedStock}
                    title={`${t("edit")} ${record.productId} ${t("counted_stock")}`}
                  >
                    <a onClick={() => handleCountedStockClick(record, date.id, text)} style={{ color: "#880e47" }}>
                      {text}
                    </a>
                  </StockEditPopover>
                );
              },
              sorter: (a, b) => utils.antdTableSorter(a, b, `${date.id}_counted`),
              onCell: () => ({
                style: {
                  ...columnStyle,
                  backgroundColor: isEvenColumn ? "rgba(136, 14, 79, 0.05)" : "rgba(0, 98, 204, 0)",
                },
              }),
              onHeaderCell: () => ({
                style: {
                  ...columnStyle,
                  backgroundColor: isEvenColumn ? "rgba(136, 14, 79, 0.05)" : "rgba(136, 14, 79, 0)",
                },
              }),
            },
            {
              title: t("ordered"),
              dataIndex: `${date.id}_requested`,
              key: `${date.id}_requested`,
              width: 110,
              render: (number, record) => {
                if (number === 0 || record.isPackaging) {
                  return <span>{number}</span>;
                }

                const relevantOrders = ordersInDateRange
                  .filter(
                    (order) =>
                      order.packingDateId === date.id &&
                      ![ORDER_STATUS_CANCELED, ORDER_STATUS_CANCELLED].includes(order.farmorderStatus),
                  )
                  .map((order) => ({ order, count: countInCart(order.userShoppingCart, record.productId) }))
                  .filter(({ count }) => count.direct + count.viaBundle > 0);
                const ordersTotal = relevantOrders.reduce((sum, { count }) => sum + count.direct + count.viaBundle, 0);
                const mismatch = ordersTotal - (number || 0);

                const popoverContent = (
                  <>
                    <ul style={{ listStyle: "none", margin: "0", padding: "0" }}>
                      {relevantOrders.map(({ order, count }) => (
                        <li key={order.id} style={{ marginBottom: "4px" }}>
                          {order.farmorderId} - {order.farmorderCustomerName}: {count.direct + count.viaBundle}
                          {count.viaBundle > 0 && ` (${count.viaBundle} ${t("via bundle")})`}
                        </li>
                      ))}
                    </ul>
                    <div
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        marginTop: "4px",
                        paddingTop: "4px",
                        fontSize: "12px",
                        color: mismatch ? "#cf1322" : "rgba(0, 0, 0, 0.45)",
                      }}
                    >
                      {t("sum of orders")}: {ordersTotal}
                      {!!mismatch && ` (${t("mismatch")}: ${mismatch > 0 ? "+" : ""}${mismatch})`}
                    </div>
                  </>
                );

                return (
                  <Popover content={popoverContent} title={`${t("total")}: ${number}`}>
                    <span>{number}</span>
                  </Popover>
                );
              },
              sorter: (a, b) => utils.antdTableSorter(a, b, `${date.id}_requested`),
              onCell: () => ({
                style: {
                  ...columnStyle,
                  backgroundColor: isEvenColumn ? "rgba(255, 165, 0, 0)" : "rgba(136, 14, 79, 0.05)",
                },
              }),
              onHeaderCell: () => ({
                style: {
                  ...columnStyle,
                  backgroundColor: isEvenColumn ? "rgba(136, 14, 79, 0)" : "rgba(136, 14, 79, 0.05)",
                },
              }),
            },
            {
              title: t("stock"),
              dataIndex: `${date.id}_current`,
              key: `${date.id}_current`,
              width: 100,
              render: (text) => <span style={{ color: text >= 1 ? "#52c41a" : "#f5222d" }}>{text}</span>,
              sorter: (a, b) => utils.antdTableSorter(a, b, `${date.id}_current`),
              onCell: () => ({
                style: {
                  ...columnStyle,
                  backgroundColor: isEvenColumn ? "rgba(136, 14, 79, 0.05)" : "rgba(0, 98, 204, 0)",
                  borderRight: "1px solid #d9d9d9",
                },
              }),
              onHeaderCell: () => ({
                style: {
                  ...columnStyle,
                  backgroundColor: isEvenColumn ? "rgba(136, 14, 79, 0.05)" : "rgba(136, 14, 79, 0)",
                  borderRight: "1px solid #d9d9d9",
                },
              }),
            },
          ],
          onHeaderCell: () => ({
            style: {
              ...columnStyle,
              borderRight: "1px solid #d9d9d9",
            },
          }),
        });
      });
    }

    return columns;
  };

  const handleSyncToExact = async () => {
    setSyncingToExact(true);

    try {
      const token = await getFirebaseIDToken();
      const config = { headers: { Authorization: token } };

      const response = await axios.post(
        stockSyncExact,
        {}, // Empty body since we're fetching data on backend
        config,
      );

      const results = response.data.data;

      // Detailed notification
      const successCount = results.successful.length;
      const failCount = results.failed.length;
      const noExactCodeCount = results.noExactItemCode.length;

      const title =
        failCount === 0 && noExactCodeCount === 0
          ? t("notifications.stock_sync_success", { count: successCount })
          : t("notifications.stock_sync_partial", {
              success: successCount,
              failed: failCount,
              noExactCode: noExactCodeCount,
            });

      const content = (
        <div>
          {successCount > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <strong style={{ color: "#52c41a" }}>
                {t("successful_sync")} ({successCount}):
              </strong>
              <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
                {results.successful.map((item, index) => (
                  <li key={index}>
                    {item.productName} ({item.productId}) - Qty: {item.quantity}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {failCount > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <strong style={{ color: "#ff4d4f" }}>
                {t("failed_sync")} ({failCount}):
              </strong>
              <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
                {results.failed.map((item, index) => (
                  <li key={index}>
                    {item.productName} ({item.productId}): {item.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {noExactCodeCount > 0 && (
            <div>
              <strong style={{ color: "#faad14" }}>
                {t("no_exact_code")} ({noExactCodeCount}):
              </strong>
              <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
                {results.noExactItemCode.map((item, index) => (
                  <li key={index}>
                    {item.productName} ({item.productId})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );

      notification[failCount === 0 && noExactCodeCount === 0 ? "success" : "warning"]({
        message: title,
        description: content,
        duration: 0,
        style: { width: "500px" },
      });
    } catch (error) {
      console.error("Error syncing stock to Exact:", error);
      message.error(error.response?.data?.message || t("notifications.failed_to_sync_stock"));
    } finally {
      setSyncingToExact(false);
    }
  };

  const showSyncConfirmation = () => {
    confirm({
      title: t("confirm_sync_title"),
      content: t("confirm_sync_message"),
      onOk: handleSyncToExact,
      onCancel: () => {},
      okText: t("yes"),
      cancelText: t("cancel"),
    });
  };

  const handleCarryOverStock = async () => {
    setCarryingOverStock(true);

    try {
      const token = await getFirebaseIDToken();
      const config = { headers: { Authorization: token } };

      const response = await axios.post(carryOverStock, {}, config);

      if (response.data.statusCode === 200) {
        message.success(t("notifications.carry_over_stock_success"));
      }
    } catch (error) {
      console.error("Error carrying over stock:", error);
      message.error(error.response?.data?.message || t("notifications.carry_over_stock_error"));
    } finally {
      setCarryingOverStock(false);
    }
  };

  const showCarryOverConfirmation = () => {
    confirm({
      title: t("confirm_carry_over_title"),
      content: t("confirm_carry_over_message"),
      onOk: handleCarryOverStock,
      onCancel: () => {},
      okText: t("yes"),
      cancelText: t("cancel"),
    });
  };

  const getMatchingDates = () => {
    if (!dateRange || dateRange.length !== 2) return [];

    const startDate = dayjs(dateRange[0]);
    const endDate = dayjs(dateRange[1]);
    const { allDates } = getAllAvailableData();

    return allDates.filter((d) => {
      const stockDate = d.dateUnix * 1000;
      return dayjs(stockDate).isSameOrAfter(startDate, "day") && dayjs(stockDate).isSameOrBefore(endDate, "day");
    });
  };

  // Exports exactly what the grid shows: the filtered rows for the selected date(s)
  const handleExport = () => {
    const dates = getMatchingDates();
    const header = [t("barcode"), t("name")];
    dates.forEach((date) => {
      header.push(
        `${date.formattedDate} ${t("counted")}`,
        `${date.formattedDate} ${t("ordered")}`,
        `${date.formattedDate} ${t("stock")}`,
      );
    });
    const rows = filteredProducts.map((row) => {
      const cells = [row.productId, row.productName];
      dates.forEach((date) => {
        cells.push(row[`${date.id}_counted`], row[`${date.id}_requested`], row[`${date.id}_current`]);
      });
      return cells;
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...rows]), "Stock");
    const range = dates.length ? `${dates[0].formattedDate}_${dates[dates.length - 1].formattedDate}` : "export";
    XLSX.writeFile(wb, `stock-${range}.xlsx`);
  };

  const { allDates } = getAllAvailableData();
  const stockCarriedOver = getTodayCarryOverStatus();

  useEffect(() => {
    filterOrdersByDateRange(dateRange);
  }, [orders, dateRange]);

  const tabItems = [
    {
      key: "products",
      label: t("product_stocks"),
      children: (
        <>
          <Flex alignItems="center" justifyContent="between" mobileFlex={true}>
            {!isMobileView ? (
              <Space wrap style={{ marginBottom: 6 }}>
                <Input
                  style={{ maxWidth: 120 }}
                  value={searchText}
                  placeholder={t("search")}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => onSearch(e.target.value)}
                />
                <StyledSelect
                  className="w-100"
                  style={{ minWidth: 140 }}
                  $isFiltered={clickedProductCompany !== "All"}
                  onChange={handleShowProductCompany}
                  placeholder={t("company")}
                  defaultValue={clickedProductCompany !== "null" ? clickedProductCompany : "All"}
                >
                  <Option value="All">{t("all_companies")}</Option>
                  {PRODUCT_COMPANY_GROUPS.map((group) => (
                    <Option key={group.value} value={group.value}>
                      {group.label}
                    </Option>
                  ))}
                </StyledSelect>
                <StyledSelect
                  className="w-100"
                  style={{ minWidth: 140 }}
                  $isFiltered={clickedProductGroup !== "All"}
                  onChange={handleShowProductGroup}
                  placeholder={t("product_group")}
                  defaultValue={clickedProductGroup !== "null" ? clickedProductGroup : "All"}
                >
                  <Option value="All">{t("all_groups")}</Option>
                  {PRODUCT_GROUPS.map((group) => (
                    <Option key={group.value} value={group.value}>
                      {t(group.label)}
                    </Option>
                  ))}
                </StyledSelect>
                <StyledSelect
                  className="w-100"
                  style={{ minWidth: 140 }}
                  $isFiltered={clickedCategory !== "All"}
                  onChange={handleShowCategory}
                  placeholder={t("category")}
                  defaultValue={clickedCategory !== "null" ? clickedCategory : "All"}
                >
                  <Option value="All">{t("all_categories")}</Option>
                  {!!categories &&
                    categories.map((elm) => (
                      <Option key={elm} value={elm}>
                        {elm}
                      </Option>
                    ))}
                </StyledSelect>
                <Popover
                  content={
                    <div style={{ padding: "8px 0" }}>
                      <RangePicker
                        value={tempDateRange}
                        onChange={(dates) => setTempDateRange(dates)}
                        disabledDate={(current) => {
                          const maxFutureDate = dayjs().add(28, "days").endOf("day");
                          return current && current.isAfter(maxFutureDate);
                        }}
                        placeholder={["Start date", "End date"]}
                        style={{ marginBottom: 8 }}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setCustomDatePickerVisible(false);
                            setTempDateRange(null);
                          }}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          disabled={!tempDateRange || tempDateRange.length !== 2}
                          onClick={() => {
                            if (tempDateRange && tempDateRange.length === 2) {
                              handleDateRangeChange(tempDateRange);
                              setCustomDatePickerVisible(false);
                              setTempDateRange(null);
                            }
                          }}
                        >
                          {t("apply")}
                        </Button>
                      </div>
                    </div>
                  }
                  title={t("select_custom_date_range")}
                  trigger="click"
                  open={customDatePickerVisible}
                  onOpenChange={(visible) => {
                    setCustomDatePickerVisible(visible);
                    if (visible) {
                      setTempDateRange(dateRange);
                    } else {
                      setTempDateRange(null);
                    }
                  }}
                >
                  <CustomButton type={activeTimeFilter === "custom" ? "secondary" : "default"}>
                    {activeTimeFilter === "custom" && dateRange
                      ? `${dayjs(dateRange[0]).format("DD/MM")} - ${dayjs(dateRange[1]).format("DD/MM")}`
                      : t("custom_range")}
                  </CustomButton>
                </Popover>
                <Tooltip title={t("upcoming_week_tooltip")}>
                  <CustomButton
                    onClick={() => applyTimeFilter("week")}
                    type={activeTimeFilter === "week" ? "secondary" : "default"}
                  >
                    {t("upcoming_week")}
                  </CustomButton>
                </Tooltip>
                <Checkbox checked={filterByInSeason} onChange={(e) => handleFilterByInSeasonChange(e.target.checked)}>
                  {t("only_show_products_in_season")}
                </Checkbox>
              </Space>
            ) : (
              <Popover
                content={
                  <FilterPopoverContent>
                    <div>
                      <FilterLabel>{t("search")}</FilterLabel>
                      <Input
                        value={searchText}
                        placeholder={t("search")}
                        prefix={<SearchOutlined />}
                        allowClear
                        onChange={(e) => onSearch(e.target.value)}
                        size="large"
                      />
                    </div>
                    <div>
                      <FilterLabel>{t("company")}</FilterLabel>
                      <Select
                        placeholder={t("company")}
                        style={{ width: "100%" }}
                        value={clickedProductCompany !== "null" ? clickedProductCompany : "All"}
                        onChange={handleShowProductCompany}
                        size="large"
                      >
                        <Option value="All">{t("all_companies")}</Option>
                        {PRODUCT_COMPANY_GROUPS.map((group) => (
                          <Option key={group.value} value={group.value}>
                            {group.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <FilterLabel>{t("product_group")}</FilterLabel>
                      <Select
                        placeholder={t("product_group")}
                        style={{ width: "100%" }}
                        value={clickedProductGroup !== "null" ? clickedProductGroup : "All"}
                        onChange={handleShowProductGroup}
                        size="large"
                      >
                        <Option value="All">{t("all_groups")}</Option>
                        {PRODUCT_GROUPS.map((group) => (
                          <Option key={group.value} value={group.value}>
                            {t(group.label)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <FilterLabel>{t("category")}</FilterLabel>
                      <Select
                        placeholder={t("category")}
                        style={{ width: "100%" }}
                        value={clickedCategory !== "null" ? clickedCategory : "All"}
                        onChange={handleShowCategory}
                        size="large"
                      >
                        <Option value="All">{t("all_categories")}</Option>
                        {!!categories &&
                          categories.map((elm) => (
                            <Option key={elm} value={elm}>
                              {elm}
                            </Option>
                          ))}
                      </Select>
                    </div>
                    <div>
                      <FilterLabel>{t("select_custom_date_range")}</FilterLabel>
                      <RangePicker
                        value={dateRange}
                        onChange={(dates) => {
                          if (dates && dates.length === 2) {
                            handleDateRangeChange(dates);
                          } else {
                            handleDateRangeChange(null);
                          }
                        }}
                        disabledDate={(current) => {
                          const maxFutureDate = dayjs().add(28, "days").endOf("day");
                          return current && current.isAfter(maxFutureDate);
                        }}
                        placeholder={["Start date", "End date"]}
                        style={{ width: "100%" }}
                        size="large"
                        popupClassName="single-month-range-picker"
                      />
                    </div>
                    <div>
                      <Checkbox
                        checked={filterByInSeason}
                        onChange={(e) => handleFilterByInSeasonChange(e.target.checked)}
                      >
                        {t("only_show_products_in_season")}
                      </Checkbox>
                    </div>
                    <FilterButtonsContainer>
                      <Button
                        onClick={() => {
                          const todayRange = [dayjs().startOf("day"), dayjs().endOf("day")];
                          localStorage.removeItem("categoryFilter");
                          localStorage.removeItem("productCompanyFilter");
                          localStorage.removeItem("productGroupFilter");
                          localStorage.removeItem("filterByInSeason");
                          setSearchText("");
                          setClickedProductCompany("All");
                          setClickedProductGroup("All");
                          setClickedCategory("All");
                          setActiveTimeFilter("today");
                          setDateRange(todayRange);
                          setFilterByInSeason(true);
                          checkIfNeedsAdditionalFetch(todayRange);
                          filterOrdersByDateRange(todayRange);
                          if (productList && productList.length > 0) {
                            const filtered = applyFiltersDirectly(
                              productList,
                              "",
                              "All",
                              "All",
                              todayRange,
                              "All",
                              true,
                            );
                            setFilteredProducts(filtered);
                          }
                          setFilterPopoverVisible(false);
                        }}
                        style={{ flex: 1 }}
                      >
                        {t("reset")}
                      </Button>
                      <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                        {t("apply")}
                      </Button>
                    </FilterButtonsContainer>
                  </FilterPopoverContent>
                }
                title={t("filters")}
                trigger="click"
                open={filterPopoverVisible}
                onOpenChange={setFilterPopoverVisible}
                placement="bottomLeft"
              >
                <Button
                  icon={<FilterOutlined />}
                  style={{
                    borderColor:
                      clickedProductCompany !== "All" ||
                      clickedProductGroup !== "All" ||
                      clickedCategory !== "All" ||
                      activeTimeFilter === "custom" ||
                      !filterByInSeason ||
                      searchText
                        ? "#FF4B33"
                        : "",
                    color:
                      clickedProductCompany !== "All" ||
                      clickedProductGroup !== "All" ||
                      clickedCategory !== "All" ||
                      activeTimeFilter === "custom" ||
                      !filterByInSeason ||
                      searchText
                        ? "#FF4B33"
                        : "",
                  }}
                />
              </Popover>
            )}
            <Space wrap style={{ marginBottom: 6 }}>
              <Tooltip title={t("batch_update_stock_tooltip")}>
                <Button onClick={() => setBatchUpdateModalVisible(true)} type="primary" disabled={!hasEditPermission}>
                  {t("batch_update_stock")}
                </Button>
              </Tooltip>
              <Tooltip title={t("sync_stock_to_exact_tooltip")}>
                <Button
                  onClick={showSyncConfirmation}
                  type="primary"
                  loading={syncingToExact}
                  disabled={!hasEditPermission || isLoading || !stockData || Object.keys(stockData).length === 0}
                >
                  <SyncOutlined />
                  {t("send_to_exact_bulk")}
                </Button>
              </Tooltip>
              <Tooltip title={stockCarriedOver ? t("carry_over_already_done") : t("carry_over_stock_tooltip")}>
                <Button
                  onClick={showCarryOverConfirmation}
                  type="primary"
                  loading={carryingOverStock}
                  disabled={!hasEditPermission || stockCarriedOver || isLoading}
                >
                  {stockCarriedOver ? "✓ " : t("carry_over_stock")}
                </Button>
              </Tooltip>
              <Button onClick={handleExport} disabled={isLoading || getMatchingDates().length === 0}>
                <DownloadOutlined />
                {t("export")}
              </Button>
            </Space>
          </Flex>
          {isLoading || productsLoading ? (
            <Loading />
          ) : (
            <div className="table-responsive" ref={tableContainerRef}>
              <StockTable
                matchingDatesCount={getMatchingDates().length}
                showSorterTooltip={false}
                columns={getTableColumns()}
                dataSource={filteredProducts}
                rowKey="key"
                sticky
                scroll={{ x: "max-content", y: tableY }}
                pagination={false}
                locale={{
                  emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_products")} />,
                }}
              />
            </div>
          )}
        </>
      ),
    },
    {
      key: "live_stock",
      label: t("live_stock.tab_label"),
      children: <LiveStockTab tableY={tableY} />,
    },
    {
      key: "packaging",
      label: t("packaging_stocks"),
      children: <PackagingStocksTab tableY={tableY} hasEditPermission={hasEditPermission} />,
    },
    {
      key: "crates",
      label: t("crates_stocks"),
      children: (
        <CrateStocksTab
          tableY={tableY}
          openCrateId={crateDrawerOpenForId}
          onCrateDrawerOpened={() => setCrateDrawerOpenForId(null)}
          onProductClick={handleProductClickFromCrateDrawer}
        />
      ),
    },
    {
      key: "crates_log",
      label: t("crate_stock.crates_log_tab"),
      children: <CratesLogTab tableY={tableY} />,
    },
  ];

  return (
    <>
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      <BatchStockUpdateModal
        visible={batchUpdateModalVisible}
        onCancel={() => setBatchUpdateModalVisible(false)}
        stockDates={allDates}
        products={products}
        categories={categories}
        stockData={stockData}
      />

      <Drawer
        title={t("crate_stock.product_crates_drawer_title", {
          productName: selectedProductForCrates?.productName || selectedProductForCrates?.productId || "",
        })}
        open={productCratesDrawerOpen}
        onClose={() => {
          setProductCratesDrawerOpen(false);
          setSelectedProductForCrates(null);
        }}
        width={isMobile ? "100%" : 700}
      >
        <Table
          columns={[
            {
              title: t("crate_stock.crate_id"),
              dataIndex: "crateId",
              key: "crateId",
              render: (value, record) => (
                <a
                  onClick={() => {
                    setProductCratesDrawerOpen(false);
                    setSelectedProductForCrates(null);
                    setActiveTab("crates");
                    setCrateDrawerOpenForId(record._crate?.crateId || value);
                  }}
                  style={{ color: "#880e47" }}
                >
                  {value}
                </a>
              ),
            },
            {
              title: t("crate_stock.package_date"),
              dataIndex: "packageDate",
              key: "packageDate",
            },
            {
              title: t("crate_stock.qty"),
              dataIndex: "quantity",
              key: "quantity",
              render: (value, record) => (
                <a
                  onClick={() => {
                    setProductAdjustingCrate(record._crate);
                    setProductAdjustingCrateItem(record._crateItem);
                    setProductAdjustModalOpen(true);
                  }}
                  style={{
                    color: "#880e47",
                    ...(isMobile
                      ? {
                          display: "inline-block",
                          padding: "12px 12px",
                          minHeight: 44,
                          lineHeight: "24px",
                        }
                      : null),
                  }}
                >
                  {value}
                </a>
              ),
            },
          ]}
          dataSource={productCratesData}
          pagination={false}
          size="small"
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("crate_stock.no_crates_for_product")} />
            ),
          }}
        />
      </Drawer>

      <AdjustCrateQuantityModal
        open={productAdjustModalOpen}
        onCancel={() => {
          setProductAdjustModalOpen(false);
          setProductAdjustingCrate(null);
          setProductAdjustingCrateItem(null);
        }}
        crate={productAdjustingCrate}
        crateItem={productAdjustingCrateItem}
        updateCrateStock={updateCrateStock}
        addCrateEvent={addCrateEvent}
      />

      <StockChangeReasonModal
        open={stockReasonModalOpen}
        onCancel={handleStockReasonCancel}
        onConfirm={handleStockReasonConfirm}
      />
    </>
  );
};

export default Stock;

const getTableMaxWidth = (matchingDatesCount) => {
  switch (matchingDatesCount) {
    case 1:
      return "570px";
    case 2:
      return "895px";
    case 3:
      return "1220px";
    case 4:
      return "1545px";
    case 5:
      return "1870px";
    case 6:
      return "2195px";
    case 7:
      return "2520px";
    default:
      return "none";
  }
};

const StockTable = styled(Table)`
  div.ant-table-body {
    max-height: 60vh !important;
  }

  max-width: ${(props) => getTableMaxWidth(props.matchingDatesCount)};

  /* Alternating row colors */
  .ant-table-tbody > tr:nth-child(odd) {
    background-color: #ffffff;
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: #fafafa;
  }

  /* Hover effects that work with both patterns */
  .ant-table-tbody > tr:hover > td {
    background-color: #f3e7ed !important;
  }
`;

const CustomButton = ({ type, children, ...props }) => {
  const getButtonStyle = () => {
    if (type === "secondary") {
      return {
        backgroundColor: "#f0f0f0",
        borderColor: "#d9d9d9",
        color: "#595959",
      };
    }
    return {};
  };

  return (
    <Button {...props} style={{ ...getButtonStyle(), ...props.style }}>
      {children}
    </Button>
  );
};

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;
